import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { devicesTableQuery, type ListName } from '../api/softsee'
import { DeviceWorkspace } from '../devices/DeviceWorkspace'
import { Content } from '../ui/Shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { ScheduleInstallForm } from './ScheduleInstallForm'
import { deviceColumns, serverColumns, type DeviceRow } from './columns'

/**
 * Listele Softsee - All devices, Workstations, Servers, Mobiles.
 * Acelasi tabel `#softseeDevices`, aceleasi coloane.
 *
 * Widgetul statiilor bifate deschide ACELASI formular ca in portalul vechi
 * (Softsee::scheduleInstallForm + saveScheduledInstall), cu statiile bifate
 * trimise mai departe. Statea aici o macheta moarta, cu un `select` gol si
 * un buton care nu scria nimic: cine bifa un teanc de statii si apasa
 * "Schedule" credea ca a programat o instalare care nu s-a facut niciodata.
 */

export const TABLE_ID = 'softseeDevices'

function SelectedDevicesWidget({ chosen, shown, onHide, onSaved }: {
  chosen: number[]
  shown: boolean
  onHide(): void
  onSaved(): void
}) {
  /**
   * PRIMA FILA SE DESCHIDE SINGURA.
   *
   * In portal, `MultipleActions.widgetContent` (public/js/general.js) se
   * termina cu `tab.eq(0).trigger('click')`: la FIECARE schimbare de bife
   * widgetul se arata deschis pe prima fila din lista, cu continutul ei deja
   * cerut de la server. Widgetul Softsee
   * (softsee/devices/multiple_select/devicesWidget.php pe `cec-master`) are o
   * singura fila - "Schedule package installation" - deci prima fila e chiar
   * aia, si formularul trebuie sa fie la vedere din clipa in care se bifeaza.
   *
   * Aici fila statea inchisa pana o apasa cineva: bifai un teanc de statii,
   * widgetul se deschidea gol, si formularul de programare parea ca lipseste.
   *
   * Fila nu mai are, deci, o stare a ei: cat timp widgetul e la vedere si are
   * macar o statie bifata, prima fila e deschisa. Nu se poate ajunge la widget
   * deschis pe nicio alta fila - fiindca alta nu exista.
   */
  const open = shown && chosen.length > 0

  return (
    <div className="multiple_check_widget widget" style={{ display: shown ? undefined : 'none' }}>
      <div className="widget-header bg-module">
        <div className="widget-icon">
          <i className="fas fa-box-open"></i>
        </div>
        <h2 className="widget-title">Selected Devices Options</h2>
        <div className="widget-action">
          <button className="btn hide-widget" type="button" onClick={onHide}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
      <div className="widget-content">
        <div style={{ width: '20%', display: 'inline-block', boxSizing: 'border-box' }}>
          <ul className="nav nav-metro-tabs tabs-left">
            <li data-class-nav="nav-content" data-toggle="tab" className={open ? 'active' : undefined}>
              {/*
                Fara href: in macheta veche legatura ducea la
                `<moduleUrl>/scheduleInstallForm` din CodeIgniter, iar acolo
                fila era deschisa de general.js. Aici fila e a paginii, deci
                legatura ramasa n-ar duce nicaieri. Si fara clic: fila e deja
                deschisa cand widgetul se vede, exact ca in portal.
              */}
              <a>Schedule package installation</a>
            </li>
          </ul>
        </div>
        <div className="nav-content" style={{ display: 'inline-block', width: '79%', boxSizing: 'border-box', verticalAlign: 'top', paddingLeft: 15 }}>
          {open ? <ScheduleInstallForm chosen={chosen} onSaved={onSaved} /> : null}
        </div>
      </div>
    </div>
  )
}

export function DeviceListPage({ list }: { list: ListName }) {
  const client = useQueryClient()
  const isServers = list === 'servers'
  const [state, setState] = useTableState(`${TABLE_ID}_${list}`, { start: 0, length: 25, order: { column: isServers ? 6 : 5, dir: 'desc' }, search: '' })
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  const [widgetHidden, setWidgetHidden] = useState(false)
  const columns = useMemo(() => (isServers ? serverColumns() : deviceColumns()), [isServers])
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[isServers ? 6 : 5]
  const query = useQuery({
    ...devicesTableQuery({
      list,
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: orderColumn.index,
      orderName: orderColumn.name ?? '',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })
  const rows = (query.data?.data ?? []) as DeviceRow[]

  const onTableClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target instanceof HTMLInputElement && target.classList.contains('oneDevice')) {
      const idNum = Number(target.value)
      setSelected((s) => {
        const next = new Set(s)
        if (target.checked) next.add(idNum)
        else next.delete(idNum)
        return next
      })
      setWidgetHidden(false)
    } else if (target instanceof HTMLInputElement && target.classList.contains('allDevices')) {
      setSelected(target.checked ? new Set(rows.map((r) => r.deviceId)) : new Set())
      setWidgetHidden(false)
    }
  }

  return (
    <Content>
      <div id="content" onClick={onTableClick}>
        <DataTable
          id={TABLE_ID}
          columnTools
          columns={columns}
          rows={rows.map((r) => ({ ...r, __checked: selected.has(r.deviceId) }))}
          total={query.data?.recordsTotal ?? 0}
          fetching={query.isFetching}
          error={query.isError ? (query.error as Error).message : null}
          onRetry={() => void query.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId)}
          details={(row) => <DeviceWorkspace id={Number(row.deviceId)} from="softsee" />}
        >
          <SelectedDevicesWidget
            chosen={[...selected]}
            shown={selected.size > 0 && !widgetHidden}
            onHide={() => setWidgetHidden(true)}
            onSaved={() => {
              /* Dupa salvare bifele se golesc si tabelul se reciteste: altfel
                 widgetul ramane deschis pe o selectie deja programata. */
              setSelected(new Set())
              void client.invalidateQueries({ queryKey: ['softsee', 'devices'] })
            }}
          />
        </DataTable>
      </div>
    </Content>
  )
}
